import { Button, Link, SxProps, Theme, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ConfirmModal } from '~/components/ConfirmModal';
import { EmptyData } from '~/components/EmptyData';
import { Pagination } from '~/components/Pagination';
import { SearchInput } from '~/components/SearchInput';
import { useNotification } from '~/hooks/useNotification';
import {
  useGetDeletedCompaniesQuery,
  useRestoreDeletedCompanyMutation,
} from '~/services/rtkQuery/admin';
import { formatUrl } from '~/utils/formats';
import { getCurrentPageInfo, getTotalPagesCount } from '~/utils/pagination';

import styles from './styles.module.scss';

const SEARCH_INPUT_SX: SxProps<Theme> = { width: '344px' };
const FETCH_LIMIT = 10;

export const DeletedCompaniesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams.get('search') || '';
  const currentPage = +(searchParams.get('page') || 1);
  const [restoreConfirmModalVisible, setRestoreConfirmModalVisible] = useState(false);
  const { showNotification, handleApiError } = useNotification();
  const activeCompanyId = useRef<number | null>(null);
  const getDeletedCompaniesQuery = useGetDeletedCompaniesQuery({
    name: searchValue.trim(),
    pageNum: currentPage,
    perPage: FETCH_LIMIT,
  });
  const [restoreDeletedCompanyMutation, { isLoading: isLoadingRestoreDeletedCompanyMutation }] =
    useRestoreDeletedCompanyMutation();

  useEffect(() => {
    if (
      currentPage > 1 &&
      getDeletedCompaniesQuery.data &&
      getDeletedCompaniesQuery.data.result.data.length === 0
    ) {
      setSearchParams(prevValue => {
        prevValue.set(
          'page',
          getTotalPagesCount({
            totalItemsCount: getDeletedCompaniesQuery.data?.result.total || 0,
            fetchLimit: FETCH_LIMIT,
          }).toString(),
        );

        return prevValue;
      });
    }
  }, [
    currentPage,
    getDeletedCompaniesQuery.data,
    getDeletedCompaniesQuery.data?.result.data.length,
    setSearchParams,
  ]);

  const handleSearchValueChange = useCallback(
    (value: string) => {
      setSearchParams(prevValue => {
        prevValue.set('search', value);
        prevValue.set('page', '1');

        return prevValue;
      });
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams(prevValue => {
        prevValue.set('page', page.toString());

        return prevValue;
      });
    },
    [setSearchParams],
  );

  const showRestoreConfirmModal = useCallback((id: number) => {
    activeCompanyId.current = id;

    setRestoreConfirmModalVisible(true);
  }, []);

  const hideRestoreConfirmModal = useCallback(() => {
    setRestoreConfirmModalVisible(false);
  }, []);

  const handleCompanyRestore = useCallback(async () => {
    try {
      if (activeCompanyId.current) {
        const response = await restoreDeletedCompanyMutation({
          id: activeCompanyId.current,
        }).unwrap();

        showNotification({
          type: 'success',
          title: response.title,
          message: response.message,
        });
      }
    } catch (e) {
      handleApiError(e);
    } finally {
      hideRestoreConfirmModal();
    }
  }, [handleApiError, hideRestoreConfirmModal, restoreDeletedCompanyMutation, showNotification]);

  const totalPages = useMemo(() => {
    return getTotalPagesCount({
      totalItemsCount: getDeletedCompaniesQuery.data?.result.total || 0,
      fetchLimit: FETCH_LIMIT,
    });
  }, [getDeletedCompaniesQuery.data?.result.total]);

  return (
    <div className={styles.container}>
      <div className={styles.container__heading}>
        <Typography color="var(--text-2-color)" fontWeight="700" variant="subtitle1">
          Deleted Companies
        </Typography>
        <SearchInput
          initialValue={searchValue}
          placeholder="Search here..."
          sx={SEARCH_INPUT_SX}
          onChange={handleSearchValueChange}
        />
      </div>
      {getDeletedCompaniesQuery.isFetching ||
      !getDeletedCompaniesQuery.data ||
      getDeletedCompaniesQuery.data.result.data.length === 0 ? (
        <EmptyData
          className={styles['container__empty-data']}
          description="No companies available"
          loading={getDeletedCompaniesQuery.isFetching}
        />
      ) : (
        <>
          <table className={styles.container__table}>
            <thead>
              <tr>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Company Name
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Website Link
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Pitchbook URL
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Deleted
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Deleted By
                  </Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {getDeletedCompaniesQuery.data?.result.data.map(company => (
                <tr key={company.id}>
                  <td>
                    <Typography
                      color="text.primary"
                      fontWeight="500"
                      lineHeight="16px"
                      variant="body1"
                    >
                      {company.name}
                    </Typography>
                  </td>
                  <td>
                    <Link
                      color="rgb(var(--main-2-color-rgb))"
                      fontWeight="500"
                      href={formatUrl(company.web)}
                      lineHeight="20px"
                      target="_blank"
                      variant="body1"
                    >
                      {company.web}
                    </Link>
                  </td>
                  <td>
                    <Link
                      color="rgb(var(--main-2-color-rgb))"
                      fontWeight="500"
                      href={formatUrl(company.pitchbook_url)}
                      lineHeight="20px"
                      target="_blank"
                      variant="body1"
                    >
                      {company.pitchbook_url}
                    </Link>
                  </td>
                  <td>
                    <Typography
                      color="text.primary"
                      fontWeight="500"
                      lineHeight="16px"
                      variant="body1"
                    >
                      {format(new Date(company.updated_at), 'MMM dd, yyyy')}
                    </Typography>
                  </td>
                  <td>
                    <div>
                      <Typography
                        color="text.primary"
                        display="inline-block"
                        fontWeight="500"
                        lineHeight="16px"
                        variant="body1"
                      >
                        {company.deleted_by?.email || 'N/A'}
                      </Typography>
                      <Button
                        size="small"
                        sx={{ width: '112px' }}
                        variant="outlined"
                        onClick={() => showRestoreConfirmModal(company.id)}
                      >
                        Restore
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.container__footer}>
            <Typography color="var(--gray-2-color)" lineHeight="14px" variant="subtitle2">
              {getCurrentPageInfo({
                fetchLimit: FETCH_LIMIT,
                currentPage,
                currentPageItemsCount: getDeletedCompaniesQuery.data?.result.data.length || 0,
                totalItemsCount: getDeletedCompaniesQuery.data?.result.total || 0,
              })}
            </Typography>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onChange={handlePageChange}
            />
          </div>
        </>
      )}
      <ConfirmModal
        description="Are you sure you want to restore this company?"
        loading={isLoadingRestoreDeletedCompanyMutation}
        open={restoreConfirmModalVisible}
        title="Do you want to restore?"
        onCancel={hideRestoreConfirmModal}
        onConfirm={handleCompanyRestore}
      />
    </div>
  );
};
